import React from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/Button';
interface InformedConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function InformedConsentModal({
  isOpen,
  onClose
}: InformedConsentModalProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 print:hidden" data-id="element-83">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-84">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-6 py-4 flex items-center justify-between" data-id="element-85">
          <h2 className="text-2xl font-bold" data-id="element-86">Informed Consent Form</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-87">
            <X className="w-6 h-6" data-id="element-88" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 text-stone-700" data-id="element-89">
          <div className="text-center mb-4" data-id="element-90">
            <h3 className="text-xl font-bold text-amber-900" data-id="element-91">
              (For Acupuncture Treatment)
            </h3>
            <p className="text-sm text-stone-600 mt-2" data-id="element-92">
              Please read this document carefully and completely. This is an
              informed consent that explains the expectations and risks
              associated with Acupuncture Treatment.
            </p>
          </div>

          <div className="space-y-4 text-sm leading-relaxed" data-id="element-93">
            <p data-id="element-94">
              I understand that the professional practitioners (acupuncturists)
              in{' '}
              <strong data-id="element-95">
                Aatmshakti Institute of Integrated Medical Research (AIIMR)
              </strong>{' '}
              will treat my conditions within their scope of practice of
              Traditional Chinese Medicine (TCM)/ Master Tung's Acupuncture /
              Auricular Medicine with seeds and Acupuncture / Dr. Tan Balance
              Acupuncture Method / Acupressure Therapy / Colour Therapy / Korean
              Cosmetic Acupuncture / Abdominal Acupuncture Scalp Acupuncture
              Neval Acupuncture/ Static Cupping /Moving Cupping / Fire Cupping /
              Blood Letting, which may limit treatment choices.
            </p>

            <p data-id="element-96">
              I understand that "Acupuncture" treatment involves the penetration
              of skin by sterile, one-time-use, disposable filiform needles.
              There are additional methods of treatment, within the scope of
              acupuncture and "Traditional Chinese Medicine" that may be used in
              addition to, instead, or in combination with needles. The scope
              includes:
            </p>

            <ul className="list-disc pl-6 space-y-2" data-id="element-97">
              <li data-id="element-98">
                Assessment by the collection of data by interviewing,
                observation, palpation, pulse taking, tongue observation and
                other methods;
              </li>
              <li data-id="element-99">
                Acupressure and therapeutic massage (Tui-Na, (Chinese massage));
              </li>
              <li data-id="element-100">
                Dietary herbal supplements and Chinese or Western herbal
                medicine;
              </li>
              <li data-id="element-101">Nutritional counseling;</li>
              <li data-id="element-102">
                Energy-flow work, exercises or other prescribed forms of
                movement (e.g. Qigong, Tai Chi);
              </li>
              <li data-id="element-103">
                Heat therapy with the use of mugwort (moxibustion) or a heat
                lamp over localized areas;
              </li>
              <li data-id="element-104">Glass cups "fire- cupping", plastic suction cups;</li>
              <li data-id="element-105">Gua Sha tools for scraping;</li>
              <li data-id="element-106">Electrical stimulation of acupuncture needles</li>
              <li data-id="element-107">LED (Light therapy)</li>
            </ul>

            <p data-id="element-108">
              I understand that as the patient, I must inform the practitioners
              of medications, medical history, and sensitivities to smoke or
              scents, if I am currently pregnant, have a bleeding disorder or
              any communicable or contagious conditions that could be passed to
              others.
            </p>

            <p data-id="element-109">
              I understand that I must lie still during treatment; am
              responsible to ask questions regarding treatment details if
              explanations provided are not clear or understood; have a right to
              refuse treatment at any point.
            </p>

            <p data-id="element-110">
              I understand that patients are advised to tell their doctor they
              are receiving TCM acupuncture, to avoid possible treatment
              conflicts. Patients are encouraged to report their outcomes to
              their doctors.
            </p>

            <p data-id="element-111">
              I understand that the assessment, diagnosis, and treatment of
              medical conditions by TCM varies from Western medicine.
            </p>

            <p data-id="element-112">
              I understand that I must give the clinic 24 hours' notice of
              cancellation otherwise I may be charged the full session fee.
            </p>

            <p data-id="element-113">
              I understand that acupuncture does not typically provide an
              instant cure. For most serious or chronic ailments, more than one
              treatment, along with some lifestyle and dietary changes, may be
              necessary for noticeable changes to take place.
            </p>

            <h4 className="text-lg font-bold text-amber-900 mt-6" data-id="element-114">
              Risks and Complications of Acupuncture Treatment
            </h4>

            <p data-id="element-115">
              I understand that there is risk of side effects, which may
              include:
            </p>

            <div className="space-y-3" data-id="element-116">
              <div data-id="element-117">
                <strong data-id="element-118">Pain</strong> - Acupuncture needling may cause an
                initial "prick" as it punctures the skin followed by a temporary
                achy sensation, numbness, or tingling at or near the needling
                sites that may radiate. These are all normal needling sensations
                and may last a few days. Please tell your practitioner if any
                sensation is too strong so they can adjust the treatment
                accordingly.
              </div>

              <div data-id="element-119">
                Tui-Na (Chinese Massage), acupuncture insertion, acupuncture
                stimulation, electro-acupuncture, cupping, Gua Sha, qigong
                (exercise), may all cause pain. Patient feedback is important
                for the practitioner to adjust the treatment when necessary;
                Electrical stimulation of acupuncture needles produces a mild
                vibration/tapping sensation on the needles, which may be
                painful;
              </div>

              <div data-id="element-120">
                <strong data-id="element-121">Bruising</strong> - Bruising may occur anytime we
                puncture the skin. If you have bruises on some area of the body,
                please inform your practitioner. The normal reaction of cupping,
                "gua sha" and scraping is to leave red marks or bruising. This
                will usually slowly resolve on it's own with 5-10 days and is
                part of the intentional therapeutic effect.
              </div>

              <div data-id="element-122">
                <strong data-id="element-123">Infection</strong> - It is possible to develop an
                infection whenever the skin is punctured despite using single
                use disposable needles and clean needle technique. Please inform
                us if you have a known immune problem so we can take special
                precautions.
              </div>

              <div data-id="element-124">
                <strong data-id="element-125">Burns & Smoke Irritation</strong> - Heat therapy and
                moxibustion may cause redness, blistering or unintentional burns
                and/or scarring. It is important to verbally communicate with
                your practitioner if any heat therapy feels too hot. Smoke from
                moxibustion may cause coughing, lung irritation, headaches or
                allergic reactions for those sensitive to scents or smoke;
              </div>

              <div data-id="element-126">
                <strong data-id="element-127">Feeling of Relaxed or Sleepy</strong> - It is common to
                feel relaxed or sleepy after treatment so avoid getting up too
                quickly and rushing anywhere directly after treatment. Give
                yourself time to adjust after treatment before driving or using
                the stairs or any other potentially dangerous activity.
              </div>

              <div data-id="element-128">
                <strong data-id="element-129">Dizziness & Fainting</strong> - Dizziness or fainting
                can occur as a result of treatment. Please tell us if you are
                prone to fainting. Important factors that can decrease this risk
                include ensuring you have had sufficient food, ensuring you have
                not had any drugs or alcohol prior to treatment, communicating
                if the stimulation is uncomfortably strong, and getting up
                slowly after treatment.
              </div>
            </div>

            <h4 className="text-lg font-bold text-amber-900 mt-6" data-id="element-130">
              Revoking consent
            </h4>

            <p data-id="element-131">
              I understand that this initial consent is to educate and explain
              my treatment and that I may verbally revoke my consent to any
              treatment at any time.
            </p>

            <p data-id="element-132">
              I therefore agree to waive any rights for legal liability that I
              could have against "Aatmshakti Institute of Integrated Medical
              Research" and/or the acupuncture therapist.
            </p>

            <p data-id="element-133">
              I hereby request and give consent for treatment at the "Aatmshakti
              Institute of Integrated Medical Research" using modalities within
              the scope of "Acupuncture" and "Traditional Chinese Medicine" as
              described above.
            </p>

            <p className="font-semibold" data-id="element-134">
              By Agreeing / signing below, I confirm I have read, understood,
              asked questions and agree to the above Terms of Treatment.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-amber-200 px-6 py-4 bg-amber-50" data-id="element-135">
          <Button onClick={onClose} className="w-full" data-id="element-136">
            Close
          </Button>
        </div>
      </div>
    </div>;
}